import type { AgentSubscription } from "schemas/agent-billing";

import * as React from "react";

import { Tag } from "@replohq/lattice/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import {
  useCurrentWorkspace,
  useCurrentWorkspaceId,
  useIsAgentBillingEnabled,
} from "@v2/contexts/WorkspaceDashboardContext";
import { agentBillingRouter } from "@v2/trpc/routes/agentBilling";

import {
  ALWAYS_FRESH,
  CopyableId,
  formatDate,
  KeyValue,
  StripeLink,
} from "./shared";

const SubscriptionDetails: React.FC<{
  activeSubscription: AgentSubscription | null | undefined;
  isLoading: boolean;
}> = ({ activeSubscription, isLoading }) => {
  if (isLoading) {
    return (
      <div className="text-muted py-3 text-xs">Loading subscription...</div>
    );
  }

  if (!activeSubscription) {
    return (
      <div className="text-muted py-3 text-xs">
        No agent billing subscription found.
      </div>
    );
  }

  return (
    <>
      <KeyValue label="Tier">
        {activeSubscription.plan ? (
          <Tag variant="neutral" size="sm">
            {activeSubscription.plan}
          </Tag>
        ) : (
          "--"
        )}
      </KeyValue>
      <KeyValue label="Interval">
        <Tag variant="neutral" size="sm">
          {activeSubscription.interval}
        </Tag>
      </KeyValue>
      <KeyValue label="Started At">
        {formatDate(activeSubscription.startedAt)}
      </KeyValue>
      <KeyValue label="Cancel At">
        {formatDate(activeSubscription.cancelAt)}
      </KeyValue>
      <KeyValue label="Period End">
        {formatDate(activeSubscription.currentPeriodEnd)}
      </KeyValue>
      <KeyValue label="Stripe Subscription ID">
        <StripeLink value={activeSubscription.stripeSubscriptionId} />
      </KeyValue>
      <KeyValue label="Stripe Customer ID">
        <StripeLink value={activeSubscription.stripeCustomerId} />
      </KeyValue>
    </>
  );
};

export const SubscriptionSummary: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId();
  const workspace = useCurrentWorkspace();
  const isAgentBilling = useIsAgentBillingEnabled();
  const { data: activeSubscription, isLoading: activeLoading } =
    agentBillingRouter.subscription.find.useQuery(
      workspaceId ? { workspaceId } : skipToken,
      ALWAYS_FRESH,
    );

  return (
    <div className="flex flex-col divide-y divide-border">
      <KeyValue label="Workspace ID">
        <CopyableId value={workspaceId} />
      </KeyValue>
      <KeyValue label="Payment Method">
        <Tag variant="neutral" size="sm">
          {workspace?.paymentProcessor ?? "none"}
        </Tag>
      </KeyValue>
      <KeyValue label="Agent Billing">
        <Tag variant={isAgentBilling ? "success" : "neutral"} size="sm">
          {isAgentBilling ? "enabled" : "disabled"}
        </Tag>
      </KeyValue>
      <SubscriptionDetails
        activeSubscription={activeSubscription}
        isLoading={activeLoading}
      />
    </div>
  );
};
